/**
 * Settings API Adapter
 *
 * Provides API-compatible interface for settings management.
 * Converts between YAML config format and frontend API format.
 */

import { readFile, writeFile } from 'fs/promises';
import { parseDocument } from 'yaml';
import { Data, Effect } from 'effect';
import {
  DEFAULT_ROLES,
  DEFAULT_WORKHORSES,
  PARENT_MODEL_REF,
  loadConfigSync,
  getGlobalConfigPath,
  clearConfigCache,
  mergeConfigs,
  type YamlConfig,
  type ModelRef,
  type RoleConfig,
  type RolesConfig,
  type WeightedModelRef,
  type WorkhorsesConfig,
  type WorkhorseSlot,
  type TtsDaemonConfig,
  type ConversationSearchConfig,
  type RoleEffort,
  type ProviderConfig,
  ROLE_EFFORTS,
} from './config-yaml.js';
import { ModelId } from './settings.js';
import type { Role } from './agents.js';
import { tieredExecutionConfigForSave, validateTieredExecutionSettings, type ApiTieredExecutionConfig } from './settings-api-tiered-execution.js';
import type { RuntimeName } from './runtimes/types.js';
import { getBuiltInDefaultHarness } from './providers.js';
import { defaultBackgroundAiFeatures, type BackgroundAiFeature } from './background-ai/registry.js';
import { MODEL_CAPABILITIES, hasModelCapabilitySync, MODEL_DEPRECATIONS, resolveModelIdSync, getModelEffortLevelsSync } from './model-capabilities.js';

/**
 * Deprecation warning in API format
 */
export interface ApiDeprecationWarning {
  workType: string;
  from: string;
  to: string;
}

export type ApiTtsConfig = Omit<TtsDaemonConfig, 'daemonPort' | 'daemonHost'>;

const API_TTS_KEYS = [
  'enabled',
  'voice',
  'statusVoice',
  'volume',
  'rate',
  'maxChars',
  'dropInfoWhenFull',
  'voiceMap',
  'mutedSources',
  'utteranceTemplates',
  'mutedIssues',
] as const satisfies readonly (keyof ApiTtsConfig)[];

const API_TTS_KEY_SET = new Set<string>(API_TTS_KEYS);

function unknownApiTtsKeys(tts: Record<string, unknown>): string[] {
  return Object.keys(tts).filter((key) => !API_TTS_KEY_SET.has(key));
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isStringRecord(value: unknown): value is Record<string, string> {
  return isRecord(value) && Object.values(value).every((entry) => typeof entry === 'string');
}

function validateApiTtsConfigFields(tts: Record<string, unknown>, errors: string[]): void {
  const unknownKeys = unknownApiTtsKeys(tts);
  if (unknownKeys.length > 0) {
    errors.push(`Unknown tts setting(s): ${unknownKeys.join(', ')}`);
  }

  if (tts.enabled !== undefined && typeof tts.enabled !== 'boolean') errors.push('tts.enabled must be a boolean');
  if (tts.dropInfoWhenFull !== undefined && typeof tts.dropInfoWhenFull !== 'boolean') errors.push('tts.dropInfoWhenFull must be a boolean');
  if (tts.voice !== undefined && typeof tts.voice !== 'string') errors.push('tts.voice must be a string');
  if (tts.statusVoice !== undefined && typeof tts.statusVoice !== 'string') errors.push('tts.statusVoice must be a string');
  if (tts.volume !== undefined && (typeof tts.volume !== 'number' || tts.volume < 0 || tts.volume > 1)) {
    errors.push('tts.volume must be between 0 and 1');
  }
  if (tts.rate !== undefined && (typeof tts.rate !== 'number' || tts.rate <= 0)) {
    errors.push('tts.rate must be greater than 0');
  }
  if (tts.maxChars !== undefined && (typeof tts.maxChars !== 'number' || tts.maxChars <= 0)) {
    errors.push('tts.maxChars must be greater than 0');
  }
  if (tts.voiceMap !== undefined && !isStringRecord(tts.voiceMap)) errors.push('tts.voiceMap must be a string record');
  if (tts.utteranceTemplates !== undefined && !isStringRecord(tts.utteranceTemplates)) errors.push('tts.utteranceTemplates must be a string record');
  if (tts.mutedSources !== undefined && !isStringArray(tts.mutedSources)) errors.push('tts.mutedSources must be an array of strings');
  if (tts.mutedIssues !== undefined && !isStringArray(tts.mutedIssues)) errors.push('tts.mutedIssues must be an array of strings');
}

function sanitizeApiTtsConfig(tts: ApiTtsConfig | undefined): ApiTtsConfig | undefined {
  if (tts === undefined) return undefined;
  if (!isRecord(tts)) throw new Error('tts must be an object');

  const errors: string[] = [];
  validateApiTtsConfigFields(tts, errors);
  if (errors.length > 0) throw new Error(errors.join('; '));

  return Object.fromEntries(
    API_TTS_KEYS
      .filter((key) => tts[key] !== undefined)
      .map((key) => [key, tts[key]]),
  ) as ApiTtsConfig;
}

export interface ApiSettingsConfig {
  workhorses?: WorkhorsesConfig;
  roles?: RolesConfig;
  models: {
    providers: {
      anthropic: boolean;
      openai: boolean;
      google: boolean;
      minimax: boolean;
      zai: boolean;
      kimi: boolean;
      mimo: boolean;
      openrouter: boolean;
      nous: boolean;
      dashscope: boolean;
    };
    /** Legacy model-route overrides are no longer surfaced by GET /api/settings. */
    overrides?: Partial<Record<string, ModelId>>;
    provider_harnesses?: ProviderHarnessesConfig;
    provider_default_harnesses?: BuiltInProviderHarnessesConfig;
    gemini_thinking_level?: number;
    default_conversation_model?: ModelId;
  };
  conversationSearch?: ConversationSearchConfig;
  conversations?: {
    compaction_model?: ModelId;
    manual_compact_mode?: 'claude-code' | 'overdeck-native';
    rich_compaction?: boolean;
    title_model?: ModelId;
    watch_dirs?: string[];
    scan_max_parallel?: number | null;
    embeddings?: boolean;
    embedding_provider?: 'openai' | 'voyage' | 'ollama';
    embedding_model?: string;
    embedding_auto_on_deep?: boolean;
    enrichment?: {
      quick_model?: string | null;
      deep_model?: string | null;
      max_parallel?: number;
      cost_confirm_threshold?: number;
    };
  };
  memory?: {
    provider?: 'anthropic' | 'cliproxy';
    model?: string;
    per_day_cost_cap_usd?: number;
    fallback_provider?: 'anthropic' | 'cliproxy' | '';
    fallback_model?: string;
    fallback_chain?: Array<{ provider: 'anthropic' | 'cliproxy'; model: string }>;
    observations_enabled?: boolean;
    prompt_time_injection_enabled?: boolean;
    rollup_pending_threshold?: number;
    sidebar_refresh_interval_ms?: number;
    worker_concurrency?: number;
  };
  /** Background AI feature toggles + low-cost master switch (PAN-1583). */
  background_ai?: {
    cheap_mode?: boolean;
    features?: Partial<Record<BackgroundAiFeature, boolean>>;
  };
  api_keys: {
    openai?: string;
    voyage?: string;
    google?: string;
    minimax?: string;
    zai?: string;
    kimi?: string;
    mimo?: string;
    openrouter?: string;
    nous?: string;
    dashscope?: string;
  };
  agents?: {
    rtk?: {
      enabled?: boolean;
    };
    tldr?: {
      enabled?: boolean;
    };
  };
  tts?: ApiTtsConfig;
  /** TTS activity-summarizer model/enabled, surfaced for the Background AI section (PAN-1589). */
  tts_summarizer?: {
    model?: ModelId;
    enabled?: boolean;
  };
  openrouter?: {
    favorites?: string[];
  };
  tmux?: {
    config_mode?: 'managed' | 'inherit-user';
  };
  tracker_keys?: {
    linear?: string;
    github?: string;
    gitlab?: string;
    rally?: string;
  };
  experimental?: {
    /** Show experimental dashboard surfaces in navigation and direct routes. */
    experimentalFeatures?: boolean;
    /** Use Claude Code Channels delivery for conversations/messages. */
    claudeCodeChannels?: boolean;
    /** Enable legacy Claude Code Channels MCP wiring for new eligible work agents. */
    claudeCodeChannelsMcp?: boolean;
    /** Render dashboard chat markdown with Streamdown instead of ReactMarkdown. */
    streamdownRenderer?: boolean;
    /** Show explicit harness/model permutations in dashboard model pickers. */
    showHarnessModelPermutations?: boolean;
  };
  /**
   * Permission mode for spawned Claude Code agents.
   *
   * 'auto' (default) → --permission-mode auto (classifier blocks destructive ops)
   * 'bypass'         → --permission-mode bypassPermissions (DSP flag removed)
   *
   * Persisted under `claude.permissionMode` in `~/.overdeck/config.yaml`.
   * Override per-invocation with `--yolo` / `--no-yolo` / `PAN_YOLO`.
   */
  claude?: {
    permissionMode?: 'auto' | 'bypass';
  };
  /**
   * Permission mode for Codex TUI conversation sessions.
   *
   * 'read-only'   → approval_policy=on-request + sandbox_mode=read-only
   * 'workspace'   → approval_policy=on-request + sandbox_mode=workspace-write (default)
   * 'auto-review' → approval_policy=on-request + approvals_reviewer=auto_review + sandbox_mode=workspace-write
   * 'full-access' → approval_policy=never + sandbox_mode=danger-full-access
   *
   * Persisted under `codex.permissionMode` in `~/.overdeck/config.yaml`.
   */
  codex?: {
    permissionMode?: 'read-only' | 'workspace' | 'auto-review' | 'full-access';
  };
  /**
   * Remote work-agent provisioning settings.
   *
   * `resiliency_tier` controls whether remote work agents use ephemeral
   * (cheaper, no disk) or durable (volume-backed, survives crashes) machines.
   * `max_concurrent_agents` caps how many remote work agents may run at once
   * (0 = unlimited).
   *
   * Persisted under `remote` in `~/.overdeck/config.yaml`.
   */
  remote?: {
    resiliency_tier?: 'ephemeral' | 'durable';
    max_concurrent_agents?: number;
  };
  tiered_execution?: ApiTieredExecutionConfig;
  deprecation_warnings?: ApiDeprecationWarning[];
}

/**
 * Load settings in API format (for GET /api/settings)
 *
 * Also detects deprecated model IDs in current overrides and returns warnings.
 */
export function getDefaultConversationModelApi(): ModelId | undefined {
  const { config } = loadConfigSync();

  if (config.defaultConversationModel) return resolveModelIdSync(config.defaultConversationModel);
  // Unset is legal (fresh install) — Settings must still render so the operator can set it (PAN-2589).
  return undefined;
}

const ROLE_NAMES: readonly Role[] = ['plan', 'work', 'review', 'test', 'ship', 'flywheel', 'strike', 'sequencer', 'knowledge'];
type AvailableModel = { id: ModelId; name: string; costPer1MTokens: number };
type AvailableModelsApi = Record<'anthropic' | 'openai' | 'google' | 'minimax' | 'zai' | 'kimi' | 'mimo' | 'openrouter' | 'nous' | 'dashscope', AvailableModel[]>;
const WORKHORSE_SLOTS: readonly WorkhorseSlot[] = ['expensive', 'mid', 'cheap'];
const MODEL_PROVIDERS = ['anthropic', 'openai', 'google', 'minimax', 'zai', 'kimi', 'mimo', 'openrouter', 'nous', 'dashscope'] as const;
type ApiModelProvider = typeof MODEL_PROVIDERS[number];
type ProviderHarnessesConfig = Partial<Record<ApiModelProvider, RuntimeName | ''>>;
type BuiltInProviderHarnessesConfig = Record<ApiModelProvider, RuntimeName>;
type RoleConfigInput = Omit<RoleConfig, 'harness'> & { harness?: RuntimeName | null | '' };

function builtInProviderHarnesses(): BuiltInProviderHarnessesConfig {
  return Object.fromEntries(
    MODEL_PROVIDERS.map((provider) => [provider, getBuiltInDefaultHarness(provider)]),
  ) as BuiltInProviderHarnessesConfig;
}

const ALLOWED_SUB_ROLES: Partial<Record<Role, readonly string[]>> = {
  work: ['inspect', 'inspect-deep'],
  review: ['security', 'performance', 'correctness', 'requirements', 'synthesis'],
};
function seededWorkhorses(config: Pick<ReturnType<typeof loadConfigSync>['config'], 'workhorses'>): WorkhorsesConfig {
  return { ...DEFAULT_WORKHORSES, ...(config.workhorses ?? {}) };
}

function seededRoles(config: Pick<ReturnType<typeof loadConfigSync>['config'], 'roles'>): RolesConfig {
  const roles: RolesConfig = {};
  for (const role of ROLE_NAMES) {
    const defaultRole = DEFAULT_ROLES[role];
    const configuredRole = config.roles?.[role];
    const sub = {
      ...(defaultRole.sub ?? {}),
      ...(configuredRole?.sub ?? {}),
    };
    roles[role] = {
      ...defaultRole,
      ...(configuredRole ?? {}),
      sub: Object.keys(sub).length > 0 ? sub : undefined,
    };
  }
  return roles;
}

function toApiTtsConfig(config: ReturnType<typeof loadConfigSync>['config']['tts']): ApiTtsConfig {
  return {
    enabled: config.enabled,
    voice: config.voice,
    statusVoice: config.statusVoice,
    volume: config.volume,
    rate: config.rate,
    maxChars: config.maxChars,
    dropInfoWhenFull: config.dropInfoWhenFull,
    voiceMap: { ...config.voiceMap },
    mutedSources: [...config.mutedSources],
    utteranceTemplates: { ...config.utteranceTemplates },
    mutedIssues: [...config.mutedIssues],
  };
}

function pruneUndefined<T>(value: T): T {
  if (Array.isArray(value)) return value.map((item) => pruneUndefined(item)) as T;
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>)
        .filter(([, entry]) => entry !== undefined)
        .map(([key, entry]) => [key, pruneUndefined(entry)]),
    ) as T;
  }
  return value;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isWorkhorseRef(ref: string): boolean {
  return ref.startsWith('workhorse:');
}

function workhorseSlotFromRef(ref: string): string {
  return ref.slice('workhorse:'.length);
}

function mergeRoles(current?: RolesConfig, updates?: RolesConfig): RolesConfig | undefined {
  if (!current && !updates) return undefined;
  const merged: RolesConfig = { ...(current ?? {}) };
  for (const [role, rawRoleConfig] of Object.entries(updates ?? {}) as Array<[Role, RoleConfigInput]>) {
    const roleConfig = normalizeRoleConfig(rawRoleConfig);
    merged[role] = {
      ...(merged[role] ?? {}),
      ...roleConfig,
      sub: roleConfig.sub
        ? {
            ...(merged[role]?.sub ?? {}),
            ...roleConfig.sub,
          }
        : merged[role]?.sub,
    };
    if (hasHarnessClearSentinel(rawRoleConfig)) {
      delete merged[role]?.harness;
    }
  }
  return merged;
}

function hasHarnessClearSentinel(roleConfig: RoleConfigInput): boolean {
  return Object.prototype.hasOwnProperty.call(roleConfig, 'harness')
    && (roleConfig.harness === null || roleConfig.harness === '');
}

function normalizeRoleConfig(roleConfig: RoleConfigInput): RoleConfig {
  const normalized = { ...roleConfig } as RoleConfig;
  if (hasHarnessClearSentinel(roleConfig)) {
    delete normalized.harness;
  }
  return normalized;
}

function normalizeRolesConfig(roles?: RolesConfig): RolesConfig | undefined {
  if (!roles) return undefined;
  const normalized: RolesConfig = {};
  for (const [role, roleConfig] of Object.entries(roles) as Array<[Role, RoleConfigInput]>) {
    normalized[role] = normalizeRoleConfig(roleConfig);
  }
  return normalized;
}

function validateModelRef(
  fieldPath: string,
  ref: unknown,
  effectiveWorkhorses: WorkhorsesConfig,
  errors: string[],
  warnings: string[],
  allowWorkhorseRef: boolean,
  allowParentRef = false,
  allowDistribution = false,
): void {
  if (Array.isArray(ref)) {
    if (!allowDistribution) {
      errors.push(`${fieldPath} distribution not allowed here; must be a scalar model reference`);
      return;
    }
    if (ref.length === 0) {
      errors.push(`${fieldPath} distribution must be a non-empty array`);
      return;
    }
    for (let i = 0; i < ref.length; i++) {
      const entry = ref[i] as Record<string, unknown>;
      if (!isRecord(entry)) {
        errors.push(`${fieldPath}[${i}] must be an object with model and weight`);
        continue;
      }
      if (!Number.isInteger(entry.weight) || (entry.weight as number) <= 0) {
        errors.push(`${fieldPath}[${i}].weight must be a positive integer`);
      }
      validateModelRef(`${fieldPath}[${i}].model`, entry.model, effectiveWorkhorses, errors, warnings, allowWorkhorseRef);
    }
    return;
  }

  if (typeof ref !== 'string' || ref.trim() === '') {
    errors.push(`${fieldPath} must be a non-empty model reference`);
    return;
  }

  if (ref === PARENT_MODEL_REF) {
    if (!allowParentRef) {
      errors.push(`${fieldPath} cannot be ${PARENT_MODEL_REF}; ${PARENT_MODEL_REF} is valid only for sub-role models`);
    }
    return;
  }

  if (isWorkhorseRef(ref)) {
    if (!allowWorkhorseRef) {
      errors.push(`${fieldPath} cannot reference another workhorse`);
      return;
    }
    const slot = workhorseSlotFromRef(ref);
    if (!WORKHORSE_SLOTS.includes(slot as WorkhorseSlot)) {
      errors.push(`${fieldPath} references unknown workhorse slot "${slot}"`);
      return;
    }
    if (!effectiveWorkhorses[slot as WorkhorseSlot]) {
      errors.push(`${fieldPath} references ${ref} but workhorses.${slot} is not defined`);
    }
    return;
  }

  if (MODEL_DEPRECATIONS[ref]) {
    warnings.push(`${fieldPath}: "${ref}" is deprecated, use "${MODEL_DEPRECATIONS[ref]}" instead`);
    return;
  }

  const resolved = resolveModelIdSync(ref);
  if (!hasModelCapabilitySync(resolved)) {
    errors.push(`Invalid model reference "${ref}" at ${fieldPath}`);
  }
}

function validateRoleFields(fieldPath: string, roleConfig: Record<string, unknown>, errors: string[]): void {
  const harness = roleConfig.harness;
  if (harness !== undefined && harness !== null && harness !== '' && harness !== 'claude-code' && harness !== 'ohmypi' && harness !== 'codex') {
    errors.push(`${fieldPath}.harness must be claude-code, ohmypi, codex, null, or empty string`);
  }

  const effort = roleConfig.effort;
  if (effort !== undefined && !ROLE_EFFORTS.includes(effort as RoleEffort)) {
    errors.push(`${fieldPath}.effort must be one of ${ROLE_EFFORTS.join(', ')}`);
  }

  const maxAgents = roleConfig.maxAgents;
  if (maxAgents !== undefined && (typeof maxAgents !== 'number' || !Number.isInteger(maxAgents) || maxAgents < 1)) {
    errors.push(`${fieldPath}.maxAgents must be a positive integer`);
  }

  const scope = roleConfig.scope;
  if (scope !== undefined && scope !== 'pan-only' && scope !== 'all-tracked-projects') {
    errors.push(`${fieldPath}.scope must be pan-only or all-tracked-projects`);
  }

  // PAN-1862: review pipeline knobs (harmless on other roles; only review reads them).
  const mode = roleConfig.mode;
  if (mode !== undefined && mode !== 'quick' && mode !== 'full' && mode !== 'none') {
    errors.push(`${fieldPath}.mode must be quick, full, or none`);
  }
  const reReviewScope = roleConfig.reReviewScope;
  if (reReviewScope !== undefined && reReviewScope !== 'all' && reReviewScope !== 'changed' && reReviewScope !== 'blockers') {
    errors.push(`${fieldPath}.reReviewScope must be all, changed, or blockers`);
  }
}

/** Resolve a role/sub-role model ref (possibly a workhorse: slot) to a concrete model id. */
function resolveModelRefToId(ref: unknown, workhorses: WorkhorsesConfig): ModelId | undefined {
  if (typeof ref !== 'string' || ref.length === 0 || ref === PARENT_MODEL_REF) return undefined;
  if (isWorkhorseRef(ref)) {
    const resolved = workhorses[workhorseSlotFromRef(ref) as WorkhorseSlot];
    if (!resolved || isWorkhorseRef(resolved)) return undefined;
    return resolveModelIdSync(resolved);
  }
  return resolveModelIdSync(ref);
}

function validateWorkhorsesAndRoles(settings: ApiSettingsConfig, errors: string[], warnings: string[]): void {
  const effectiveWorkhorses: WorkhorsesConfig = { ...DEFAULT_WORKHORSES };

  if (settings.workhorses !== undefined) {
    if (!isRecord(settings.workhorses)) {
      errors.push('workhorses must be an object');
    } else {
      for (const [slot, ref] of Object.entries(settings.workhorses)) {
        if (!WORKHORSE_SLOTS.includes(slot as WorkhorseSlot)) {
          errors.push(`Unknown workhorse slot "${slot}"`);
          continue;
        }
        effectiveWorkhorses[slot as WorkhorseSlot] = ref as ModelRef;
        validateModelRef(`workhorses.${slot}`, ref, effectiveWorkhorses, errors, warnings, false);
      }
    }
  }

  if (settings.roles !== undefined) {
    if (!isRecord(settings.roles)) {
      errors.push('roles must be an object');
    } else {
      for (const [roleName, rawRoleConfig] of Object.entries(settings.roles)) {
        if (!ROLE_NAMES.includes(roleName as Role)) {
          errors.push(`Unknown role "${roleName}"`);
          continue;
        }
        const role = roleName as Role;
        if (!isRecord(rawRoleConfig)) {
          errors.push(`roles.${role}.model must be a non-empty model reference`);
          continue;
        }

        validateModelRef(`roles.${role}.model`, rawRoleConfig.model, effectiveWorkhorses, errors, warnings, true, false, true);
        validateRoleFields(`roles.${role}`, rawRoleConfig, errors);

        // Model-aware effort: reject levels the role's resolved model doesn't accept.
        // For a distribution, every entry must support the effort.
        const effort = rawRoleConfig.effort;
        if (typeof effort === 'string' && ROLE_EFFORTS.includes(effort as RoleEffort)) {
          const modelRef = rawRoleConfig.model ?? DEFAULT_ROLES[role]?.model;
          if (Array.isArray(modelRef)) {
            for (const entry of modelRef as WeightedModelRef[]) {
              const resolvedModel = resolveModelRefToId(entry.model, effectiveWorkhorses);
              if (resolvedModel) {
                const supported = getModelEffortLevelsSync(resolvedModel);
                if (supported !== undefined && !supported.includes(effort as RoleEffort)) {
                  errors.push(
                    `roles.${role}.effort '${effort}' is not supported by ${resolvedModel} (supported: ${supported.join(', ')})`,
                  );
                }
              }
            }
          } else {
            const resolvedModel = resolveModelRefToId(modelRef, effectiveWorkhorses);
            if (resolvedModel) {
              const supported = getModelEffortLevelsSync(resolvedModel);
              if (supported !== undefined && !supported.includes(effort as RoleEffort)) {
                errors.push(
                  `roles.${role}.effort '${effort}' is not supported by ${resolvedModel} (supported: ${supported.join(', ')})`,
                );
              }
            }
          }
        }

        if (rawRoleConfig.sub !== undefined) {
          if (!isRecord(rawRoleConfig.sub)) {
            errors.push(`roles.${role}.sub must be an object`);
          } else {
            const allowedSubRoles = ALLOWED_SUB_ROLES[role] ?? [];
            for (const [subRole, rawSubConfig] of Object.entries(rawRoleConfig.sub)) {
              if (!allowedSubRoles.includes(subRole)) {
                errors.push(`Unknown sub-role "${subRole}" for role "${role}"`);
                continue;
              }
              if (!isRecord(rawSubConfig)) {
                errors.push(`roles.${role}.sub.${subRole}.model must be a non-empty model reference`);
                continue;
              }
              validateModelRef(
                `roles.${role}.sub.${subRole}.model`,
                rawSubConfig.model,
                effectiveWorkhorses,
                errors,
                warnings,
                true,
                true,
              );
            }
          }
        }
      }
    }
  }

  if (errors.length === 0) {
    try {
      mergeConfigs({ workhorses: effectiveWorkhorses, roles: settings.roles });
    } catch (error) {
      errors.push(error instanceof Error ? error.message : String(error));
    }
  }
}

export function loadSettingsApi(): ApiSettingsConfig {
  const { config } = loadConfigSync();

  // Detect deprecated models in current overrides. Overrides are no longer
  // surfaced by GET /api/settings, but warnings help users clean stale config.
  const deprecationWarnings: ApiDeprecationWarning[] = [];
  for (const [workType, modelId] of Object.entries(config.overrides)) {
    if (modelId && MODEL_DEPRECATIONS[modelId]) {
      deprecationWarnings.push({
        workType,
        from: modelId,
        to: MODEL_DEPRECATIONS[modelId],
      });
    }
  }

  const conversationSettings = pruneUndefined({
    compaction_model: config.conversations?.compactionModel,
    manual_compact_mode: config.conversations?.manualCompactMode,
    rich_compaction: config.conversations?.richCompaction,
    title_model: config.conversations?.titleModel,
    watch_dirs: config.conversations?.watchDirs,
    scan_max_parallel: config.conversations?.scanMaxParallel,
    embeddings: config.conversations?.embeddings,
    embedding_provider: config.conversations?.embeddingProvider,
    embedding_model: config.conversations?.embeddingModel,
    embedding_auto_on_deep: config.conversations?.embeddingAutoOnDeep,
    enrichment: config.conversations?.enrichment ? pruneUndefined({
      quick_model: config.conversations.enrichment.quickModel,
      deep_model: config.conversations.enrichment.deepModel,
      max_parallel: config.conversations.enrichment.maxParallel,
      cost_confirm_threshold: config.conversations.enrichment.costConfirmThreshold,
    }) : undefined,
  });

  const memory = config.memory ?? {
    extraction: { fallbackChain: [] },
    observationsEnabled: true,
    promptTimeInjectionEnabled: true,
    rollupPendingThreshold: 4,
    sidebarRefreshIntervalMs: 10_000,
    workerConcurrency: 4,
  };

  return {
    workhorses: seededWorkhorses(config),
    roles: seededRoles(config),
    models: {
      providers: {
        anthropic: config.enabledProviders.has('anthropic'),
        openai: config.enabledProviders.has('openai'),
        google: config.enabledProviders.has('google'),
        minimax: config.enabledProviders.has('minimax'),
        zai: config.enabledProviders.has('zai'),
        kimi: config.enabledProviders.has('kimi'),
        mimo: config.enabledProviders.has('mimo'),
        openrouter: config.enabledProviders.has('openrouter'),
        nous: config.enabledProviders.has('nous'),
        dashscope: config.enabledProviders.has('dashscope'),
      },
      provider_harnesses: config.providerHarnesses,
      provider_default_harnesses: builtInProviderHarnesses(),
      gemini_thinking_level: config.geminiThinkingLevel,
      default_conversation_model: getDefaultConversationModelApi(),
    },
    api_keys: config.apiKeys,
    agents: {
      rtk: {
        enabled: config.rtk?.enabled ?? false,
      },
      tldr: {
        enabled: config.tldr?.enabled ?? true,
      },
    },
    tts: toApiTtsConfig(config.tts),
    tts_summarizer: {
      model: config.ttsSummarizer?.model,
      enabled: config.ttsSummarizer?.enabled,
    },
    openrouter: {
      favorites: config.openrouterFavorites,
    },
    tmux: {
      config_mode: config.tmux.configMode,
    },
    conversationSearch: config.conversationSearch,
    conversations: conversationSettings,
    memory: {
      provider: memory.extraction.provider,
      model: memory.extraction.model,
      per_day_cost_cap_usd: memory.extraction.perDayCostCapUsd,
      fallback_provider: memory.extraction.fallbackChain[0]?.provider ?? '',
      fallback_model: memory.extraction.fallbackChain[0]?.model,
      fallback_chain: memory.extraction.fallbackChain,
      observations_enabled: memory.observationsEnabled,
      prompt_time_injection_enabled: memory.promptTimeInjectionEnabled,
      rollup_pending_threshold: memory.rollupPendingThreshold,
      sidebar_refresh_interval_ms: memory.sidebarRefreshIntervalMs,
      worker_concurrency: memory.workerConcurrency,
    },
    background_ai: {
      // Defensive — older test mocks of loadConfig may not include `backgroundAi`;
      // production loader always populates it via DEFAULT_CONFIG.
      cheap_mode: config.backgroundAi?.cheapMode ?? true,
      features: { ...defaultBackgroundAiFeatures(), ...config.backgroundAi?.features },
    },
    tracker_keys: config.trackerKeys,
    experimental: {
      experimentalFeatures: config.experimental?.experimentalFeatures ?? false,
      claudeCodeChannels: config.experimental?.claudeCodeChannels ?? false,
      claudeCodeChannelsMcp: config.experimental?.claudeCodeChannelsMcp ?? false,
      streamdownRenderer: config.experimental?.streamdownRenderer ?? false,
      showHarnessModelPermutations: config.experimental?.showHarnessModelPermutations ?? false,
    },
    claude: {
      // Defensive — older test mocks of loadConfig may not include `claude`;
      // production loader always populates it via DEFAULT_CONFIG.
      permissionMode: config.claude?.permissionMode ?? 'bypass',
    },
    codex: {
      permissionMode: (config.codex?.permissionMode ?? 'auto-review') as 'read-only' | 'workspace' | 'auto-review' | 'full-access',
    },
    remote: {
      resiliency_tier: config.remote?.resiliencyTier ?? 'ephemeral',
      max_concurrent_agents: config.remote?.maxConcurrentAgents ?? 0,
    },
    tiered_execution: config.tieredExecution,
    deprecation_warnings: deprecationWarnings.length > 0 ? deprecationWarnings : undefined,
  };
}

async function writeYamlConfigPreservingComments(yamlConfig: YamlConfig): Promise<void> {
  const configPath = getGlobalConfigPath();
  let existingContent = '{}\n';
  try {
    const content = await readFile(configPath, 'utf-8');
    existingContent = content.trim().length > 0 ? content : '{}\n';
  } catch (error) {
    if (!(error instanceof Error) || !('code' in error) || error.code !== 'ENOENT') {
      throw error;
    }
  }

  const doc = parseDocument(existingContent);
  if (doc.contents === null) {
    doc.contents = parseDocument('{}\n').contents;
  }
  const config = pruneUndefined(yamlConfig);

  doc.setIn(['workhorses'], config.workhorses ?? {});
  doc.setIn(['roles'], config.roles ?? {});
  doc.setIn(['models', 'providers'], config.models?.providers ?? {});
  doc.deleteIn(['models', 'overrides']);

  if (config.models?.gemini_thinking_level !== undefined) {
    doc.setIn(['models', 'gemini_thinking_level'], config.models.gemini_thinking_level);
  } else {
    doc.deleteIn(['models', 'gemini_thinking_level']);
  }

  if (config.models?.default_conversation_model !== undefined) {
    doc.setIn(['models', 'default_conversation_model'], config.models.default_conversation_model);
  } else {
    doc.deleteIn(['models', 'default_conversation_model']);
  }

  const topLevelSections: Array<[keyof YamlConfig, unknown]> = [
    ['api_keys', config.api_keys],
    ['openrouter', config.openrouter],
    ['tmux', config.tmux],
    ['conversationSearch', config.conversationSearch],
    ['conversations', config.conversations],
    ['memory', config.memory],
    ['background_ai', config.background_ai],
    ['tracker_keys', config.tracker_keys],
    ['experimental', config.experimental],
    ['claude', config.claude],
    ['codex', config.codex],
    ['tiered_execution', config.tiered_execution],
  ];

  for (const [key, value] of topLevelSections) {
    if (value === undefined) {
      doc.deleteIn([key]);
    } else {
      doc.setIn([key], value);
    }
  }

  if (config.agents?.rtk !== undefined) {
    doc.setIn(['agents', 'rtk'], config.agents.rtk);
  }

  if (config.agents?.tldr !== undefined) {
    doc.setIn(['agents', 'tldr'], config.agents.tldr);
  }

  if (config.tts !== undefined) {
    for (const [key, value] of Object.entries(config.tts)) {
      doc.setIn(['tts', key], value);
    }
  }

  if (config.remote !== undefined) {
    doc.setIn(['remote'], config.remote);
  } else {
    doc.deleteIn(['remote']);
  }

  await writeFile(configPath, doc.toString({ lineWidth: 120 }), 'utf-8');
}

function providerConfigForSave(
  provider: ApiModelProvider,
  enabled: boolean,
  settings: ApiSettingsConfig,
  currentConfig: ReturnType<typeof loadConfigSync>['config'],
): boolean | ProviderConfig {
  const auth = currentConfig.providerAuth?.[provider];
  const plan = currentConfig.providerPlan?.[provider];
  const harnessValue = settings.models.provider_harnesses?.[provider];
  const harness = harnessValue === '' ? undefined : harnessValue;
  if (!auth && !plan && harness === undefined) return enabled;
  return pruneUndefined({ enabled, auth, plan, harness });
}

async function saveSettingsApiPromise(settings: ApiSettingsConfig): Promise<void> {
  const { config: currentConfig } = loadConfigSync();

  // Convert API format to YAML format
  const yamlConfig: YamlConfig = {
    workhorses: settings.workhorses,
    roles: normalizeRolesConfig(settings.roles),
    models: {
      providers: {
        anthropic: providerConfigForSave('anthropic', settings.models.providers.anthropic, settings, currentConfig),
        openai: providerConfigForSave('openai', settings.models.providers.openai, settings, currentConfig),
        google: providerConfigForSave('google', settings.models.providers.google, settings, currentConfig),
        minimax: providerConfigForSave('minimax', settings.models.providers.minimax, settings, currentConfig),
        zai: providerConfigForSave('zai', settings.models.providers.zai, settings, currentConfig),
        kimi: providerConfigForSave('kimi', settings.models.providers.kimi, settings, currentConfig),
        mimo: providerConfigForSave('mimo', settings.models.providers.mimo, settings, currentConfig),
        openrouter: providerConfigForSave('openrouter', settings.models.providers.openrouter, settings, currentConfig),
        nous: providerConfigForSave('nous', settings.models.providers.nous, settings, currentConfig),
        dashscope: providerConfigForSave('dashscope', settings.models.providers.dashscope, settings, currentConfig),
      },
      gemini_thinking_level: settings.models.gemini_thinking_level as 1 | 2 | 3 | 4,
      default_conversation_model: settings.models.default_conversation_model,
    },
    api_keys: {
      openai: settings.api_keys.openai,
      voyage: settings.api_keys.voyage,
      google: settings.api_keys.google,
      minimax: settings.api_keys.minimax,
      zai: settings.api_keys.zai,
      kimi: settings.api_keys.kimi,
      mimo: settings.api_keys.mimo,
      openrouter: settings.api_keys.openrouter,
      nous: settings.api_keys.nous,
      dashscope: settings.api_keys.dashscope,
    },
    agents: (settings.agents?.rtk !== undefined || settings.agents?.tldr !== undefined)
      ? {
          ...(settings.agents?.rtk !== undefined ? { rtk: { enabled: settings.agents.rtk.enabled ?? false } } : {}),
          ...(settings.agents?.tldr !== undefined ? { tldr: { enabled: settings.agents.tldr.enabled ?? true } } : {}),
        }
      : undefined,
    tts: settings.tts_summarizer
      ? { ...(sanitizeApiTtsConfig(settings.tts) ?? {}), summarizer: {
          ...(settings.tts_summarizer.model ? { model: settings.tts_summarizer.model } : {}),
          ...(settings.tts_summarizer.enabled !== undefined ? { enabled: settings.tts_summarizer.enabled } : {}),
        } }
      : sanitizeApiTtsConfig(settings.tts),
    openrouter: settings.openrouter,
    tmux: settings.tmux,
    conversationSearch: settings.conversationSearch,
    conversations: settings.conversations,
    memory: settings.memory
      ? {
          extraction: {
            provider: settings.memory.provider || undefined,
            model: settings.memory.model || undefined,
            per_day_cost_cap_usd: settings.memory.per_day_cost_cap_usd,
            fallback_chain: settings.memory.fallback_chain ?? (settings.memory.fallback_provider && settings.memory.fallback_model
              ? [{ provider: settings.memory.fallback_provider, model: settings.memory.fallback_model }]
              : undefined),
          },
          features: {
            observations: settings.memory.observations_enabled,
            prompt_time_injection: settings.memory.prompt_time_injection_enabled,
          },
          rollup_pending_threshold: settings.memory.rollup_pending_threshold,
          sidebar_refresh_interval_ms: settings.memory.sidebar_refresh_interval_ms,
          worker_concurrency: settings.memory.worker_concurrency,
        }
      : undefined,
    background_ai: settings.background_ai
      ? {
          cheap_mode: settings.background_ai.cheap_mode,
          features: settings.background_ai.features,
        }
      : undefined,
    tracker_keys: settings.tracker_keys,
    experimental: settings.experimental
      ? {
          experimentalFeatures: settings.experimental.experimentalFeatures,
          claudeCodeChannels: settings.experimental.claudeCodeChannels,
          claudeCodeChannelsMcp: settings.experimental.claudeCodeChannelsMcp,
          streamdownRenderer: settings.experimental.streamdownRenderer,
          showHarnessModelPermutations: settings.experimental.showHarnessModelPermutations,
        }
      : undefined,
    claude: settings.claude?.permissionMode
      ? { permissionMode: settings.claude.permissionMode }
      : undefined,
    codex: settings.codex?.permissionMode
      ? { permissionMode: settings.codex.permissionMode }
      : undefined,
    remote: settings.remote,
    tiered_execution: tieredExecutionConfigForSave(settings.tiered_execution, currentConfig.providerAuth),
  };

  await writeYamlConfigPreservingComments(yamlConfig);

  // Clear the config-yaml cache because mtime-based invalidation can miss rapid
  // writes (same-millisecond) or coarse filesystem mtime resolution.
  clearConfigCache();
}

async function updateSettingsApiPromise(updates: Partial<ApiSettingsConfig>): Promise<ApiSettingsConfig> {
  const current = loadSettingsApi();

  // Merge updates
  const merged: ApiSettingsConfig = {
    workhorses: {
      ...current.workhorses,
      ...updates.workhorses,
    },
    roles: mergeRoles(current.roles, updates.roles),
    models: {
      ...current.models,
      ...updates.models,
      providers: {
        ...current.models.providers,
        ...updates.models?.providers,
      },
      provider_harnesses: {
        ...current.models.provider_harnesses,
        ...updates.models?.provider_harnesses,
      },
      overrides: undefined,
    },
    api_keys: {
      ...current.api_keys,
      ...updates.api_keys,
    },
    agents: {
      ...current.agents,
      ...updates.agents,
      rtk: {
        ...current.agents?.rtk,
        ...updates.agents?.rtk,
      },
      tldr: {
        ...current.agents?.tldr,
        ...updates.agents?.tldr,
      },
    },
    tts: {
      ...current.tts,
      ...sanitizeApiTtsConfig(updates.tts),
    },
    tts_summarizer: {
      ...current.tts_summarizer,
      ...updates.tts_summarizer,
    },
    openrouter: {
      ...current.openrouter,
      ...updates.openrouter,
    },
    tmux: {
      ...current.tmux,
      ...updates.tmux,
    },
    conversationSearch: {
      ...current.conversationSearch,
      ...updates.conversationSearch,
    },
    conversations: {
      ...current.conversations,
      ...updates.conversations,
    },
    memory: {
      ...current.memory,
      ...updates.memory,
    },
    background_ai: {
      cheap_mode: updates.background_ai?.cheap_mode ?? current.background_ai?.cheap_mode,
      features: {
        ...current.background_ai?.features,
        ...updates.background_ai?.features,
      },
    },
    tracker_keys: {
      ...current.tracker_keys,
      ...updates.tracker_keys,
    },
    experimental: {
      ...current.experimental,
      ...updates.experimental,
    },
    claude: {
      ...current.claude,
      ...updates.claude,
    },
    codex: {
      ...current.codex,
      ...updates.codex,
    },
    remote: {
      ...current.remote,
      ...updates.remote,
    },
  };

  // Save and return
  await Effect.runPromise(saveSettingsApi(merged));
  return merged;
}

export function getRoleConfig(role: Role): RoleConfig | undefined {
  return loadSettingsApi().roles?.[role];
}

async function setRoleConfigPromise(role: Role, roleConfig: RoleConfig): Promise<ApiSettingsConfig> {
  return Effect.runPromise(updateSettingsApi({ roles: { [role]: roleConfig } }));
}

async function updateProviderApiKeyPromise(
  provider: 'openai' | 'voyage' | 'google' | 'minimax' | 'zai' | 'kimi' | 'mimo' | 'openrouter' | 'nous' | 'dashscope',
  apiKey?: string
): Promise<ApiSettingsConfig> {
  return Effect.runPromise(updateSettingsApi({
    api_keys: {
      [provider]: apiKey,
    },
  }));
}

/**
 * Validation result with errors and warnings
 */
export interface ValidationResult {
  valid: boolean;
  errors: string[];
  warnings: string[];
}

/**
 * Validate settings from API format
 *
 * Returns errors for invalid settings and warnings for deprecated model IDs.
 */
export function validateSettingsApi(settings: ApiSettingsConfig): ValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Validate providers
  if (!settings.models?.providers) {
    errors.push('Missing providers configuration');
  } else {
    // At least one provider must be enabled
    const enabledCount = Object.values(settings.models.providers).filter(Boolean).length;
    if (enabledCount === 0) {
      errors.push('At least one provider must be enabled');
    }
  }

  if (settings.models?.provider_harnesses !== undefined) {
    if (!isRecord(settings.models.provider_harnesses)) {
      errors.push('models.provider_harnesses must be an object');
    } else {
      for (const [provider, harness] of Object.entries(settings.models.provider_harnesses)) {
        if (!MODEL_PROVIDERS.includes(provider as ApiModelProvider)) {
          errors.push(`Unknown provider harness entry "${provider}"`);
          continue;
        }
        if (harness !== undefined && harness !== '' && harness !== 'claude-code' && harness !== 'ohmypi' && harness !== 'codex') {
          errors.push(`models.provider_harnesses.${provider} must be claude-code, ohmypi, codex, or empty string`);
        }
      }
    }
  }

  // Validate overrides - check that model IDs are valid (including deprecated ones)
  if (settings.models?.overrides) {
    const validModelIds = Object.keys(MODEL_CAPABILITIES);
    for (const [workType, modelId] of Object.entries(settings.models.overrides)) {
      if (!modelId) continue;

      // Check if deprecated
      if (MODEL_DEPRECATIONS[modelId]) {
        warnings.push(
          `${workType}: "${modelId}" is deprecated, use "${MODEL_DEPRECATIONS[modelId]}" instead`
        );
      }
      // Check if valid (current or deprecated)
      else if (!validModelIds.includes(modelId)) {
        errors.push(`Invalid model ID "${modelId}" for work type "${workType}"`);
      }
    }
  }

  validateWorkhorsesAndRoles(settings, errors, warnings);

  // Validate gemini thinking level
  if (settings.models?.gemini_thinking_level !== undefined) {
    const level = settings.models.gemini_thinking_level;
    if (level < 1 || level > 4) {
      errors.push('Gemini thinking level must be between 1 and 4');
    }
  }

  if (settings.agents !== undefined) {
    if (!isRecord(settings.agents)) {
      errors.push('agents must be an object');
    } else {
      if (settings.agents.rtk !== undefined) {
        if (!isRecord(settings.agents.rtk)) {
          errors.push('agents.rtk must be an object');
        } else if (settings.agents.rtk.enabled !== undefined && typeof settings.agents.rtk.enabled !== 'boolean') {
          errors.push('agents.rtk.enabled must be a boolean');
        }
      }
      if (settings.agents.tldr !== undefined) {
        if (!isRecord(settings.agents.tldr)) {
          errors.push('agents.tldr must be an object');
        } else if (settings.agents.tldr.enabled !== undefined && typeof settings.agents.tldr.enabled !== 'boolean') {
          errors.push('agents.tldr.enabled must be a boolean');
        }
      }
    }
  }

  if (settings.memory !== undefined) {
    if (settings.memory.per_day_cost_cap_usd !== undefined && settings.memory.per_day_cost_cap_usd < 0) {
      errors.push('memory.per_day_cost_cap_usd must be greater than or equal to 0');
    }
    if (settings.memory.rollup_pending_threshold !== undefined && (!Number.isInteger(settings.memory.rollup_pending_threshold) || settings.memory.rollup_pending_threshold < 1)) {
      errors.push('memory.rollup_pending_threshold must be a positive integer');
    }
    if (settings.memory.sidebar_refresh_interval_ms !== undefined && (!Number.isInteger(settings.memory.sidebar_refresh_interval_ms) || settings.memory.sidebar_refresh_interval_ms < 1)) {
      errors.push('memory.sidebar_refresh_interval_ms must be a positive integer');
    }
    if (settings.memory.worker_concurrency !== undefined && (!Number.isInteger(settings.memory.worker_concurrency) || settings.memory.worker_concurrency < 1)) {
      errors.push('memory.worker_concurrency must be a positive integer');
    }
  }

  // Validate experimental flags — every flag must be a boolean if present.
  if (settings.experimental !== undefined) {
    if (typeof settings.experimental !== 'object' || settings.experimental === null) {
      errors.push('experimental must be an object');
    } else {
      const experimental = settings.experimental as { experimentalFeatures?: unknown; claudeCodeChannels?: unknown; claudeCodeChannelsMcp?: unknown; streamdownRenderer?: unknown; showHarnessModelPermutations?: unknown };
      if (experimental.experimentalFeatures !== undefined && typeof experimental.experimentalFeatures !== 'boolean') {
        errors.push('experimental.experimentalFeatures must be a boolean');
      }
      if (experimental.claudeCodeChannels !== undefined && typeof experimental.claudeCodeChannels !== 'boolean') {
        errors.push('experimental.claudeCodeChannels must be a boolean');
      }
      if (experimental.claudeCodeChannelsMcp !== undefined && typeof experimental.claudeCodeChannelsMcp !== 'boolean') {
        errors.push('experimental.claudeCodeChannelsMcp must be a boolean');
      }
      if (experimental.streamdownRenderer !== undefined && typeof experimental.streamdownRenderer !== 'boolean') {
        errors.push('experimental.streamdownRenderer must be a boolean');
      }
      if (experimental.showHarnessModelPermutations !== undefined && typeof experimental.showHarnessModelPermutations !== 'boolean') {
        errors.push('experimental.showHarnessModelPermutations must be a boolean');
      }
    }
  }

  if (settings.tts !== undefined) {
    if (!isRecord(settings.tts)) {
      errors.push('tts must be an object');
    } else {
      validateApiTtsConfigFields(settings.tts, errors);
    }
  }

  if (settings.remote !== undefined) {
    if (!isRecord(settings.remote)) {
      errors.push('remote must be an object');
    } else {
      if (
        settings.remote.resiliency_tier !== undefined &&
        settings.remote.resiliency_tier !== 'ephemeral' &&
        settings.remote.resiliency_tier !== 'durable'
      ) {
        errors.push('remote.resiliency_tier must be ephemeral or durable');
      }
      if (
        settings.remote.max_concurrent_agents !== undefined &&
        (!Number.isInteger(settings.remote.max_concurrent_agents) || settings.remote.max_concurrent_agents < 0)
      ) {
        errors.push('remote.max_concurrent_agents must be a non-negative integer');
      }
    }
  }

  if (settings.tiered_execution !== undefined) {
    const tieredExecutionError = validateTieredExecutionSettings(settings.tiered_execution, loadConfigSync().config.providerAuth);
    if (tieredExecutionError) errors.push(tieredExecutionError);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings,
  };
}

/**
 * Get available models by provider (for model selection UI)
 */
export function getAvailableModelsApi(): AvailableModelsApi {
  const result: AvailableModelsApi = {
    anthropic: [],
    openai: [],
    google: [],
    minimax: [],
    zai: [],
    kimi: [],
    mimo: [],
    openrouter: [],
    nous: [],
    dashscope: [],
  };

  for (const [modelId, capability] of Object.entries(MODEL_CAPABILITIES)) {
    // Skip deprecated models — they should not appear in user-facing pickers.
    // MODEL_DEPRECATIONS is the single source of truth for "this model has
    // been retired and remapped to a current one"; capability entries are kept
    // for back-compat (cost/capability lookups for old configs and historical
    // conversations), but they must not surface in dropdowns.
    if (capability.displayName.includes('(deprecated)')) continue;
    if (modelId in MODEL_DEPRECATIONS) continue;
    const entry = { id: modelId as ModelId, name: capability.displayName, costPer1MTokens: capability.costPer1MTokens };
    switch (capability.provider) {
      case 'anthropic':
        result.anthropic.push(entry);
        break;
      case 'openai':
        result.openai.push(entry);
        break;
      case 'google':
        result.google.push(entry);
        break;
      case 'kimi':
        result.kimi.push(entry);
        break;
      case 'minimax':
        result.minimax.push(entry);
        break;
      case 'zai':
        result.zai.push(entry);
        break;
      case 'mimo':
        result.mimo.push(entry);
        break;
      case 'openrouter':
        result.openrouter.push(entry);
        break;
      case 'nous':
        result.nous.push(entry);
        break;
      case 'dashscope':
        result.dashscope.push(entry);
        break;
    }
  }

  // Order OpenAI models with latest family first: 5.6-sol (current default) → 5.6-terra → 5.6-luna → 5.5 → 5.4 → 5.3-codex → 5.2 → o-series → gpt-4o legacy.
  const openaiOrder: Record<string, number> = {
    'gpt-5.6-sol': 0, 'gpt-5.6-terra': 1, 'gpt-5.6-luna': 2,
    'gpt-5.5': 10, 'gpt-5.5-pro': 11,
    'gpt-5.4': 20, 'gpt-5.4-pro': 21, 'gpt-5.4-mini': 22,
    'gpt-5.3-codex': 30,
    'gpt-5.2': 40,
    'o3': 50, 'o4-mini': 51,
    'gpt-4o': 60, 'gpt-4o-mini': 61,
  };
  result.openai.sort((a, b) => (openaiOrder[a.id] ?? 99) - (openaiOrder[b.id] ?? 99));

  return result;
}

/**
 * Get optimal default settings (for "Restore optimal defaults" feature)
 */
export function getOptimalDefaultsApi(): ApiSettingsConfig {
  return {
    workhorses: { ...DEFAULT_WORKHORSES },
    roles: seededRoles({ roles: undefined }),
    models: {
      providers: {
        anthropic: true,
        openai: false,
        google: false,
        minimax: false,
        zai: false,
        kimi: true, // Kimi K2.6 (K2.6-code-preview) used for exploration, testing, and documentation
        mimo: false,
        openrouter: false,
        nous: false,
        dashscope: false,
      },
      gemini_thinking_level: 3,
    },
    api_keys: {},
    tracker_keys: {},
  };
}

/**
 * MiniMax-optimized defaults: use MiniMax M2.7 for all work, Anthropic disabled
 */
export function getMiniMaxDefaultsApi(): ApiSettingsConfig {
  return {
    workhorses: {
      expensive: 'minimax-m2.7-highspeed',
      mid: 'minimax-m2.7-highspeed',
      cheap: 'minimax-m2.7-highspeed',
    },
    roles: seededRoles({ roles: undefined }),
    models: {
      providers: {
        anthropic: false,
        openai: false,
        google: false,
        zai: false,
        kimi: false,
        minimax: true,
        mimo: false,
        openrouter: false,
        nous: false,
        dashscope: false,
      },
      gemini_thinking_level: 3,
    },
    api_keys: {},
    tracker_keys: {},
  };
}

async function saveOpenRouterFavoritesPromise(favorites: string[]): Promise<void> {
  const current = loadSettingsApi();
  await Effect.runPromise(saveSettingsApi({
    ...current,
    openrouter: { ...current.openrouter, favorites },
  }));
}

/**
 * Get OpenRouter favorites from config
 */
export function getOpenRouterFavorites(): string[] {
  const settings = loadSettingsApi();
  return settings.openrouter?.favorites ?? [];
}

// ─── Effect variants (PAN-1249) ───────────────────────────────────────────────
//
// Additive Effect-channel variants for the genuinely-async settings-api
// surfaces. Sync helpers (`loadSettingsApi`, `getRoleConfig`, validation,
// defaults) remain unwrapped — they're pure reads of an in-memory parsed
// config. Only the disk-writing helpers receive Effect variants.

/** Tagged error for settings-api Effect variants. */
export class SettingsApiError extends Data.TaggedError('SettingsApiError')<{
  readonly operation: string;
  readonly message: string;
  readonly cause?: unknown;
}> {}

/** Effect variant of `saveSettingsApi`. */
export const saveSettingsApi = (
  settings: ApiSettingsConfig,
): Effect.Effect<void, SettingsApiError> =>
  Effect.tryPromise({
    try: () => saveSettingsApiPromise(settings),
    catch: (cause) =>
      new SettingsApiError({
        operation: 'saveSettingsApi',
        message: cause instanceof Error ? cause.message : String(cause),
        cause,
      }),
  });

/** Effect variant of `updateSettingsApi`. */
export const updateSettingsApi = (
  updates: Partial<ApiSettingsConfig>,
): Effect.Effect<ApiSettingsConfig, SettingsApiError> =>
  Effect.tryPromise({
    try: () => updateSettingsApiPromise(updates),
    catch: (cause) =>
      new SettingsApiError({
        operation: 'updateSettingsApi',
        message: cause instanceof Error ? cause.message : String(cause),
        cause,
      }),
  });

/** Effect variant of `setRoleConfig`. */
export const setRoleConfig = (
  role: Role,
  roleConfig: RoleConfig,
): Effect.Effect<ApiSettingsConfig, SettingsApiError> =>
  Effect.tryPromise({
    try: () => setRoleConfigPromise(role, roleConfig),
    catch: (cause) =>
      new SettingsApiError({
        operation: 'setRoleConfig',
        message: cause instanceof Error ? cause.message : String(cause),
        cause,
      }),
  });

/** Effect variant of `updateProviderApiKey`. */
export const updateProviderApiKey = (
  ...args: Parameters<typeof updateProviderApiKeyPromise>
): Effect.Effect<Awaited<ReturnType<typeof updateProviderApiKeyPromise>>, SettingsApiError> =>
  Effect.tryPromise({
    try: () => updateProviderApiKeyPromise(...args),
    catch: (cause) =>
      new SettingsApiError({
        operation: 'updateProviderApiKey',
        message: cause instanceof Error ? cause.message : String(cause),
        cause,
      }),
  });

/** Effect variant of `saveOpenRouterFavorites`. */
export const saveOpenRouterFavorites = (
  favorites: string[],
): Effect.Effect<void, SettingsApiError> =>
  Effect.tryPromise({
    try: () => saveOpenRouterFavoritesPromise(favorites),
    catch: (cause) =>
      new SettingsApiError({
        operation: 'saveOpenRouterFavorites',
        message: cause instanceof Error ? cause.message : String(cause),
        cause,
      }),
  });
